import { AppContext, BookingData, ServiceDuration, TimeSlot } from "../../../../AppDataProvider/AppDataProvider";
import React, { useContext, useState } from "react";
import { CalendarView } from "../CalendarView";
import { NetworkStatus } from "@apollo/client";
import { PartRequired } from "../../../../utils/types";
import { requireBookingDataFields } from "../../../../utils";
import { useApolloClient } from "@apollo/client";
import { useGetAvailableDates } from "../../hooks/useGetAvailableDates";
type CompleteStepOpts = {
  attendees: number;
  duration: ServiceDuration;
  selectedTimeSlot: TimeSlot;
};
type CalendarViewProps = {
  createReservationLoading: boolean;
  onCompleteStep: (options: CompleteStepOpts) => void;
  onGoBack: () => void;
};
type AvailabilityStepBookingData = PartRequired<BookingData, "service">;

/**
 * An abstracted data layer that sits above the
 * CalendarView component. This allows us to control
 * the execution of the useGetAvailableDates hook.
 *
 * Having this component at the same layer as the
 * UI can cause issues with multiple re-renders and
 * in some cases infinite loops.
 */
export function CalendarViewData({
  createReservationLoading,
  onCompleteStep,
  onGoBack
}: CalendarViewProps): React.ReactElement {
  const client = useApolloClient();
  const {
    appData
  } = useContext(AppContext);
  requireBookingDataFields<AvailabilityStepBookingData>(appData.bookingData, ["service"]);
  const {
    service
  } = appData.bookingData;
  const {
    durations
  } = service;

  /**
   * The number of spaces required on a booking
   */
  const [attendees, setAttendees] = useState<number>(appData.bookingData.attendees ?? 1);
  const defaultDuration = appData.bookingData.duration ? appData.bookingData.duration : durations.find(duration => duration.default) ?? durations[0];

  /**
   * The duration of a booking
   */
  const [duration, setDuration] = useState<ServiceDuration>(defaultDuration);

  /**
   * Indicates the current month being used by the availability query.
   */
  const [availabilityMonthOffset, setAvailabilityMonthOffset] = useState<number>(0);

  /**
   * Indicates the current month being displayed on the date picker.
   */
  const [monthOffset, setMonthOffset] = useState<number>(0);

  /**
   * Indicates whether the user searched further ahead for availability at least once.
   */
  const [hasSearchedFurtherAhead, setHasSearchedFurtherAhead] = useState<boolean>(false);
  const onGetMoreAvailableDates = () => {
    setHasSearchedFurtherAhead(true);
    void getMoreAvailability({
      monthOffset,
      setAvailabilityMonthOffset,
      setMonthOffset
    });
  };
  const {
    availableDates,
    getMoreAvailability,
    networkStatus,
    loading
  } = useGetAvailableDates({
    attendees,
    availabilityMonthOffset,
    bookingData: appData.bookingData,
    bookingTool: appData.bookingTool,
    duration
  });
  const onAttendeesChange = (newAttendees: number) => {
    void client.clearStore().then(() => {
      setAttendees(newAttendees);
    });
  };
  const onDurationChange = (selectedDuration: ServiceDuration) => {
    void client.clearStore().then(() => {
      setDuration(selectedDuration);
    });
  };
  const onMonthChange = (newMonthOffset: number) => {
    if (networkStatus === NetworkStatus.ready) {
      /**
       * When moving between months it's possible that we will retain
       * out of date availability due to the concatenation pagination style.
       * We need to reset the cache after every month change to ensure we always
       * have a list of availableDates that is as up to date as possible.
       *
       * Make sure we don't have an in-flight request as we could end up
       * trying to clear the cache when it doesn't exist,
       * this will result in a fatal error.
       */

      void client.clearStore().then(() => {
        setAvailabilityMonthOffset(newMonthOffset);
        setMonthOffset(newMonthOffset);
      });
    }
  };
  return <CalendarView availableDates={availableDates} attendees={attendees} duration={duration} createReservationLoading={createReservationLoading} loading={loading} hasSearchedFurtherAhead={hasSearchedFurtherAhead} monthOffset={monthOffset} onAttendeesChange={onAttendeesChange} onDurationChange={onDurationChange} onCompleteStep={onCompleteStep} onGetMoreAvailableDates={onGetMoreAvailableDates} onGoBack={onGoBack} onMonthChange={onMonthChange} data-sentry-element="CalendarView" data-sentry-component="CalendarViewData" data-sentry-source-file="CalendarViewData.tsx" />;
}